import { z } from "zod"
import {
  ServiceBikeBrakeSystem,
  ServiceBikeGearSystem,
  ServiceBikeType,
} from "./service-bike"
import { ServiceAction } from "./service-part-action"

export enum PartCategory {
  BRAKES = "BRAKES",
  DRIVETRAIN = "DRIVETRAIN",
  WHEELS_AND_TIRES = "WHEELS_AND_TIRES",
  FRAME_AND_SUSPENSION = "FRAME_AND_SUSPENSION",
  CONTACT_SURFACES = "CONTACT_SURFACES",
  ELECTRICITY = "ELECTRICITY",
  OTHER = "OTHER",
}

export enum ServiceBikePartGearSystemRequirement {
  MUST_HAVE_FRONT_GEARS = "MUST_HAVE_FRONT_GEARS",
  MUST_NOT_HAVE_FRONT_GEARS = "MUST_NOT_HAVE_FRONT_GEARS",
  NO_REQUIREMENT = "NO_REQUIREMENT",
}

export enum ElectricFrontGearsRequirement {
  REQUIRES_ELECTRIC_FRONT_GEARS = "REQUIRES_ELECTRIC_FRONT_GEARS",
  REQUIRES_MECHANIC_FRONT_GEARS = "REQUIRES_MECHANIC_FRONT_GEARS",
  NO_REQUIREMENT = "NO_REQUIREMENT",
}

export enum ElectricRearGearsRequirement {
  REQUIRES_ELECTRIC_REAR_GEARS = "REQUIRES_ELECTRIC_REAR_GEARS",
  REQUIRES_MECHANIC_REAR_GEARS = "REQUIRES_MECHANIC_REAR_GEARS",
  NO_REQUIREMENT = "NO_REQUIREMENT",
}

export enum FrontSuspensionRequirement {
  REQUIRES_FRONT_SUSPENSION = "REQUIRES_FRONT_SUSPENSION",
  REQUIRES_NO_FRONT_SUSPENSION = "REQUIRES_NO_FRONT_SUSPENSION",
  NO_REQUIREMENT = "NO_REQUIREMENT",
}

export const ServiceBikePartSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  image: z.string().url(),
  sortPriority: z.number(),
  lifespanMonths: z.number(),
  category: z.nativeEnum(PartCategory),

  /** Types the part is preselected on in inspection. Empty = every type it exists on */
  preselectedOnBikeTypes: z.array(z.nativeEnum(ServiceBikeType)),
  /** Types the part exists on at all. OTHER always holds every part */
  existsOnBikeTypes: z.array(z.nativeEnum(ServiceBikeType)),
  foundOnFrontBrakeSystems: z.array(z.nativeEnum(ServiceBikeBrakeSystem)),
  foundOnRearBrakeSystems: z.array(z.nativeEnum(ServiceBikeBrakeSystem)),
  foundOnGearSystems: z.array(z.nativeEnum(ServiceBikeGearSystem)),

  gearSystemRequirement: z.nativeEnum(ServiceBikePartGearSystemRequirement),

  availableActions: z.array(z.nativeEnum(ServiceAction)),

  frontSuspensionRequirement: z
    .nativeEnum(FrontSuspensionRequirement)
    .default(FrontSuspensionRequirement.NO_REQUIREMENT),
  requiresRearSuspension: z.boolean(),
  requiresElectricBike: z.boolean(),
  isHiddenFromPriceList: z.boolean().default(false),
  requiresManualAdd: z.boolean().default(false),
  electricFrontGearsRequirement: z
    .nativeEnum(ElectricFrontGearsRequirement)
    .default(ElectricFrontGearsRequirement.NO_REQUIREMENT),
  electricRearGearsRequirement: z
    .nativeEnum(ElectricRearGearsRequirement)
    .default(ElectricRearGearsRequirement.NO_REQUIREMENT),
})

export type ServiceBikePart = z.infer<typeof ServiceBikePartSchema>

export const ALL_BIKE_TYPES: ServiceBikeType[] = Object.values(ServiceBikeType)

/**
 * Whether a part exists on the given type at all. OTHER ("Annan", also used
 * for store custom types) always holds every part, and a missing type means
 * nothing is known yet so nothing is ruled out.
 */
export const partExistsOnBikeType = (
  part: { existsOnBikeTypes: ServiceBikeType[] },
  type: ServiceBikeType | null | undefined,
): boolean => {
  if (!type || type === ServiceBikeType.OTHER) return true
  return part.existsOnBikeTypes.includes(type)
}

/**
 * Whether a part is preselected (shown by default in inspection) on the given
 * type. An empty list means preselected on every type.
 */
export const isPartPreselectedOnBikeType = (
  part: { preselectedOnBikeTypes: ServiceBikeType[] },
  type: ServiceBikeType | null | undefined,
): boolean => {
  if (!type || part.preselectedOnBikeTypes.length === 0) return true
  return part.preselectedOnBikeTypes.includes(type)
}

/**
 * Preselected types the part doesn't exist on. A part can only be preselected
 * on types it exists on; OTHER always counts as existing.
 */
export const findPreselectedTypesOutsideExists = (
  preselectedOnBikeTypes: ServiceBikeType[],
  existsOnBikeTypes: ServiceBikeType[],
): ServiceBikeType[] =>
  preselectedOnBikeTypes.filter(
    (type) => !partExistsOnBikeType({ existsOnBikeTypes }, type),
  )

export interface BikeInfoFilter {
  type?: ServiceBikeType
  isElectric?: boolean | null
  bikeHasFrontGears?: boolean | null
  bikeHasDamperFront?: boolean | null
  bikeHasDamperBack?: boolean | null
  hasElectricFrontGears?: boolean | null
  hasElectricRearGears?: boolean | null
  brakeSystemFront?: ServiceBikeBrakeSystem | null
  brakeSystemBack?: ServiceBikeBrakeSystem | null
  gearSystem?: ServiceBikeGearSystem | null
}

/**
 * The part fields that decide whether a part physically belongs on a bike with
 * a given brake / gear / suspension / electric configuration. Both the full
 * catalog {@link ServiceBikePart} and the lighter inspection-prerequisite DTO
 * satisfy this shape (enum arrays are assignable to `string[]`), so frontend
 * and backend can share one relevance test.
 */
export interface PartBikeConfigRelevance {
  foundOnFrontBrakeSystems: string[]
  foundOnRearBrakeSystems: string[]
  foundOnGearSystems: string[]
  gearSystemRequirement: string
  frontSuspensionRequirement: string
  requiresRearSuspension: boolean
  electricFrontGearsRequirement: string
  electricRearGearsRequirement: string
}

/** The bike configuration a part is matched against. */
export interface BikeConfigRelevance {
  brakeSystemFront?: ServiceBikeBrakeSystem | null
  brakeSystemBack?: ServiceBikeBrakeSystem | null
  gearSystem?: ServiceBikeGearSystem | null
  bikeHasFrontGears?: boolean | null
  hasElectricFrontGears?: boolean | null
  hasElectricRearGears?: boolean | null
  bikeHasDamperFront?: boolean | null
  bikeHasDamperBack?: boolean | null
}

/**
 * Whether a part belongs on a bike with the given brake / gear / suspension /
 * electric configuration. This is the relevance test the inspection step 2 uses
 * to decide which part cards to show the mechanic; the inspection save persists
 * exactly the parts that pass it, so the public listing never shows mutually
 * exclusive parts (e.g. both rim "Bromsklossar" and disc "Bromsbelägg").
 *
 * Unlike {@link filterPartsByBikeInfo} this deliberately does NOT consider
 * `requiresManualAdd`, `requiresElectricBike` or bike type — those gate which
 * parts are offered by default, not whether a part fits the chosen config.
 */
export const isPartRelevantForBikeConfig = (
  part: PartBikeConfigRelevance,
  config: BikeConfigRelevance,
): boolean => {
  // Filter by front gears requirement
  if (
    config.bikeHasFrontGears === false &&
    part.gearSystemRequirement ===
      ServiceBikePartGearSystemRequirement.MUST_HAVE_FRONT_GEARS
  ) {
    return false
  }
  if (
    config.bikeHasFrontGears === true &&
    part.gearSystemRequirement ===
      ServiceBikePartGearSystemRequirement.MUST_NOT_HAVE_FRONT_GEARS
  ) {
    return false
  }

  // Filter by front suspension requirement
  if (
    config.bikeHasDamperFront === false &&
    part.frontSuspensionRequirement ===
      FrontSuspensionRequirement.REQUIRES_FRONT_SUSPENSION
  ) {
    return false
  }
  if (
    config.bikeHasDamperFront === true &&
    part.frontSuspensionRequirement ===
      FrontSuspensionRequirement.REQUIRES_NO_FRONT_SUSPENSION
  ) {
    return false
  }
  if (config.bikeHasDamperBack === false && part.requiresRearSuspension) {
    return false
  }

  // Filter by electric front gears requirements
  if (
    config.hasElectricFrontGears === false &&
    part.electricFrontGearsRequirement ===
      ElectricFrontGearsRequirement.REQUIRES_ELECTRIC_FRONT_GEARS
  ) {
    return false
  }
  if (
    config.hasElectricFrontGears === true &&
    part.electricFrontGearsRequirement ===
      ElectricFrontGearsRequirement.REQUIRES_MECHANIC_FRONT_GEARS
  ) {
    return false
  }

  // Filter by electric rear gears requirements
  if (
    config.hasElectricRearGears === false &&
    part.electricRearGearsRequirement ===
      ElectricRearGearsRequirement.REQUIRES_ELECTRIC_REAR_GEARS
  ) {
    return false
  }
  if (
    config.hasElectricRearGears === true &&
    part.electricRearGearsRequirement ===
      ElectricRearGearsRequirement.REQUIRES_MECHANIC_REAR_GEARS
  ) {
    return false
  }

  // Filter by front brake system (only if part has specific systems)
  if (
    config.brakeSystemFront &&
    part.foundOnFrontBrakeSystems.length > 0 &&
    !part.foundOnFrontBrakeSystems.includes(config.brakeSystemFront)
  ) {
    return false
  }

  // Filter by rear brake system (only if part has specific systems)
  if (
    config.brakeSystemBack &&
    part.foundOnRearBrakeSystems.length > 0 &&
    !part.foundOnRearBrakeSystems.includes(config.brakeSystemBack)
  ) {
    return false
  }

  // Filter by gear system (only if part has specific systems)
  if (
    config.gearSystem &&
    part.foundOnGearSystems.length > 0 &&
    !part.foundOnGearSystems.includes(config.gearSystem)
  ) {
    return false
  }

  return true
}

/**
 * Filters parts based on bike info. Returns only parts that are relevant
 * for the given bike configuration.
 */
export function filterPartsByBikeInfo<T extends ServiceBikePart>(
  parts: T[],
  bikeInfo: BikeInfoFilter,
): T[] {
  const { type, isElectric } = bikeInfo

  return parts.filter((part) => {
    // Filter out parts that require manual addition
    if (part.requiresManualAdd) {
      return false
    }

    // Filter by electric bike requirement
    if (isElectric === false && part.requiresElectricBike) {
      return false
    }

    if (
      !partExistsOnBikeType(part, type) ||
      !isPartPreselectedOnBikeType(part, type)
    ) {
      return false
    }

    return isPartRelevantForBikeConfig(part, bikeInfo)
  })
}
